import { AddArticlePayload, Category, Tag } from 'types/Document';
import { Form, Modal, TreeSelect, Input, Upload, UploadProps, UploadFile, Select, message } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { VendorsSearch } from '../VendorsSearch';
import { InboxOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd/es';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import moment from 'moment/moment';
import { useArticles } from 'hooks/useArticles';

const { Dragger } = Upload;

interface ArticleFormProps {
  articleId?: number;
  onSubmit: (values: any) => void;
  onCancel: () => void;
  allCategories: Category[];
  tags: Tag[];
  isLoading?: boolean;
}

export const ArticleForm: React.FC<ArticleFormProps> = ({
  articleId,
  onCancel,
  onSubmit,
  allCategories,
  tags,
  isLoading = false
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = React.useState<any[]>([]);
  const { useArticle } = useArticles();
  const { data: article } = useArticle(articleId || 0, {
    enabled: !!articleId
  });

  const treeData = useMemo(() => {
    return allCategories.map((category) => ({
      title: category.name,
      value: category.id,
      key: category.id,
      children:
        category.otherLovFileCategory?.map((subCategory) => ({
          title: subCategory.name,
          value: subCategory.id,
          key: subCategory.id
        })) || []
    }));
  }, [allCategories]);

  const customRequestHandler = async (options: RcCustomRequestOptions) => {
    const { onSuccess } = options;
    const file = options.file as RcFile;
    if (onSuccess) {
      onSuccess(console.log);
    }
    const formData = new FormData();
    formData.append('file', file);
  };

  const uploadChangeHandler = useCallback((info: UploadChangeParam<UploadFile>) => {
    const { status } = info.file;
    if (status === 'removed') {
      setFileList([]);
    } else if (status && status !== 'error') {
      setFileList(info.fileList);
    }
  }, []);

  const handleEditSubmit = (values: AddArticlePayload) => {
    // Filter valid supplier IDs
    const validSupplierIds = values.suppliers?.length
      ? values.suppliers.map((v: any) => v?.value).filter((id: any) => typeof id === 'number' && !isNaN(id))
      : [];

    // Filter valid tag IDs
    const validTagIds = values.tags?.length
      ? values.tags.filter((tag: any) => typeof tag === 'number' && !isNaN(tag))
      : [];

    const payload = {
      name: values.name,
      description: values.description,
      fileCategoryId: values.file_category_id,
      tags: validTagIds,
      suppliers: validSupplierIds,
      publicationDate: values.publication_date ? moment(values.publication_date).toISOString() : undefined,
      shareWithClient: values.publication_date ? moment(values.publication_date).isSameOrBefore(moment(), 'day') : false
    };

    return onSubmit(payload);
  };
  const handleSubmit = (values: AddArticlePayload) => {
    if (article) return handleEditSubmit(values);

    if (!fileList.length && !article) return message.error('Please upload a file');
    const formData = new FormData();

    formData.append('file', fileList[0].originFileObj as RcFile);
    formData.append('name', values.name);
    formData.append('description', values.description || '');
    formData.append('fileCategoryId', values.file_category_id?.toString() || '');

    // Handle publication date - convert Moment object to ISO string
    let isPublished = false;
    if (values.publication_date) {
      const pubDate = moment.isMoment(values.publication_date)
        ? values.publication_date
        : moment(values.publication_date);
      // Validate Moment object is valid before converting
      if (pubDate.isValid()) {
        formData.append('publicationDate', pubDate.utc().toISOString());
        isPublished = pubDate.isSameOrBefore(moment(), 'day');
      }
    }
    formData.append('shareWithClient', isPublished.toString());
    if (values.tags?.length) {
      // Filter valid tag IDs
      const validTagIds = values.tags.filter((tag: any) => typeof tag === 'number' && !isNaN(tag));
      if (validTagIds.length > 0) {
        formData.append('tags', JSON.stringify(validTagIds));
      }
    }
    if (values.suppliers?.length) {
      // Filter out any invalid supplier IDs and send only valid numbers
      const validSupplierIds = values.suppliers
        .map((v: any) => v?.value)
        .filter((id: any) => typeof id === 'number' && !isNaN(id));
      if (validSupplierIds.length > 0) {
        formData.append('suppliers', JSON.stringify(validSupplierIds));
      }
    }

    return onSubmit(formData);
  };

  const validateFileType = ({ type }: UploadFile, allowedTypes?: string) => {
    if (!allowedTypes) {
      return true;
    }

    if (type) {
      return allowedTypes.includes(type);
    }
  };

  const uploadProps: UploadProps = {
    name: 'files',
    multiple: false,
    fileList,
    onChange: uploadChangeHandler,
    beforeUpload: (file: UploadFile) => {
      const isAllowedType = validateFileType(file, 'application/pdf');
      if (!isAllowedType) {
        setFileList([]);
        message.error(`${file.name} is not a PDF file`).then();
        return false;
      }
    },
    customRequest: customRequestHandler
  };

  const formInitialValues = useMemo(
    () => ({
      file_category_id: article?.lovFileCategory?.id,
      name: article?.name,
      description: article?.description,
      tags: article?.fileTag?.map((t) => t?.tag?.id),
      suppliers: article?.vendorFile?.map((v) => ({
        label: v?.vendor?.name,
        value: v?.vendor?.id
      })),
      publication_date: article?.publicationDate ? moment(article?.publicationDate) : undefined
    }),
    [article]
  );

  return (
    <div>
      <Modal
        width={640}
        title={articleId ? 'Edit Article' : 'Add a New Article'}
        open
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        onCancel={onCancel}
        okText="Save"
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={handleSubmit}
          initialValues={formInitialValues}
        >
          <Form.Item
            name="file_category_id"
            label="Article Type"
            rules={[{ required: true, message: 'Please enter Article type' }]}
          >
            <TreeSelect size="large" treeData={treeData} placeholder="Article Type" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Article Title"
            rules={[{ required: true, message: 'Please enter Article name' }]}
          >
            <Input size="large" className="rounded-md" />
          </Form.Item>
          <Form.Item name="description" label="Short Description">
            <Input.TextArea size="large" className="rounded-md" />
          </Form.Item>{' '}
          <Form.Item name="tags" label="Add Tags">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Select"
              allowClear
              showSearch
              fieldNames={{ label: 'name', value: 'id' }}
              options={tags}
              maxTagCount={5}
              size="large"
            />
          </Form.Item>
          <Form.Item name={'suppliers'} label={'Supplier'}>
            <VendorsSearch
              labelInValue
              allowClear={true}
              placeholder={'Search for supplier'}
              style={{ width: '100%' }}
              mode={'tags'}
              maxTagCount={5}
            />
          </Form.Item>
          <Form.Item name="publication_date" label="Publication Date">
            <DatePicker size="large" className="rounded-md" showToday />
          </Form.Item>
          {!article ? (
            <Dragger {...uploadProps} className="p-2 mb-4" maxCount={1} style={{ height: 200 }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag file to this area to upload</p>
            </Dragger>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};
